import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DevicesIcon from "@mui/icons-material/Devices";
import TimelineIcon from "@mui/icons-material/Timeline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Layout from "../../components/layout/Layout";
import { api } from "../../api/api";
import { tr } from "../../i18n";
import { useContextUserId } from "../../hooks/useSupportContext";
import { StatTile, StatusBadge, UptimeMeter } from "./StatsParts";
import ScreenDetail from "./ScreenDetail";
import AlertsTab from "./AlertsTab";
import { formatAgo, formatDuration, formatNumber, formatPct } from "./statsFormat";
import "./statistics.css";

function windowLabel(w) {
  if (!w || (w.startHour <= 0 && w.endHour >= 24)) return tr("all day");
  const pad = (n) => String(n).padStart(2, "0");
  return tr("{from}–{to}", { from: pad(w.startHour), to: pad(w.endHour) });
}

function ProblemList({ problems, internal, now, onOpen }) {
  if (!problems.length) {
    return (
      <div className="stats-all-good">
        <CheckCircleOutlineIcon aria-hidden="true" />
        {tr("No problems right now. All screens are online.")}
      </div>
    );
  }
  return (
    <ul className="stats-problems">
      {problems.map((p) => (
        <li key={`${p.kind}-${p.screenId}`}>
          <button type="button" onClick={() => onOpen(p.screenId)}>
            <StatusBadge kind={p.kind} />
            <span className="stats-name">{p.name || tr("Screen")}</span>
            {internal && p.customer && <span className="stats-muted">{p.customer}</span>}
            <span className="stats-problem-when">
              {p.kind === "offline"
                ? tr("offline for {time}", { time: formatDuration(now - p.since) })
                : tr("{n} self-heals or load errors in 24 h", { n: p.count })}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function Overview({ data, onOpen }) {
  const { totals, uptime } = data;
  const win = windowLabel(data.window);
  return (
    <div className="stats-tab">
      <section className="stats-tiles">
        <StatTile
          icon={<DevicesIcon fontSize="small" />}
          label={tr("Online now")}
          value={`${formatNumber(totals.online)} / ${formatNumber(totals.screens)}`}
          help={totals.offline ? tr("{n} offline", { n: formatNumber(totals.offline) }) : tr("All screens online")}
        />
        <StatTile
          icon={<TimelineIcon fontSize="small" />}
          label={tr("Uptime 7 days")}
          value={formatPct(uptime.days7.pct)}
          help={tr("Opening hours {window}", { window: win })}
          tone="purple"
        />
        <StatTile
          icon={<TimelineIcon fontSize="small" />}
          label={tr("Uptime 30 days")}
          value={formatPct(uptime.days30.pct)}
          help={tr("{pct} around the clock", { pct: formatPct(uptime.days30AllDay.pct) })}
          tone="blue"
        />
        <StatTile
          icon={<ReportProblemOutlinedIcon fontSize="small" />}
          label={tr("Outages 30 days")}
          value={formatNumber(uptime.days30.incidents)}
          help={uptime.days30.longestOutageMs ? tr("Longest {time}", { time: formatDuration(uptime.days30.longestOutageMs) }) : tr("Longer than 5 minutes")}
          tone="bronze"
        />
      </section>

      <div className="stats-chart-row">
        <Paper elevation={0} className="stats-card">
          <h3>{tr("Current problems")}</h3>
          <ProblemList problems={data.problems} internal={data.internal} now={data.generatedAt} onOpen={onOpen} />
        </Paper>
        <Paper elevation={0} className="stats-card">
          <h3>{tr("Lowest uptime, 30 days")}</h3>
          <LowestUptime screens={data.screens} onOpen={onOpen} />
        </Paper>
      </div>
      <p className="stats-footnote">
        {tr("Uptime means the screen had contact with Notiz. Measured since status logging started; time before that is not counted.")}
      </p>
    </div>
  );
}

function LowestUptime({ screens, onOpen }) {
  const rows = screens.filter((s) => s.uptime30 != null).sort((a, b) => a.uptime30 - b.uptime30).slice(0, 5);
  if (!rows.length) return <p className="stats-muted">{tr("No uptime data yet.")}</p>;
  return (
    <ul className="stats-toplist">
      {rows.map((s) => (
        <li key={s.id}>
          <button type="button" onClick={() => onOpen(s.id)}>
            <span className="stats-name">{s.name || tr("Screen")}</span>
            <UptimeMeter pct={s.uptime30} />
          </button>
        </li>
      ))}
    </ul>
  );
}

function ScreensTab({ data, onOpen }) {
  const [search, setSearch] = useState("");
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? data.screens.filter((s) => [s.name, s.location, s.customer, s.code, s.mac].some((v) => v && String(v).toLowerCase().includes(q)))
      : data.screens;
    // Offline first, then lowest uptime.
    return [...list].sort((a, b) => (a.online - b.online) || ((a.uptime30 ?? 101) - (b.uptime30 ?? 101)));
  }, [data.screens, search]);

  return (
    <div className="stats-tab">
      <div className="stats-toolbar">
        <TextField
          size="small"
          placeholder={data.internal ? tr("Screen, place or customer") : tr("Screen or place")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 240 }}
        />
        <span className="stats-muted">{tr("{n} screens · uptime in opening hours {window}", { n: rows.length, window: windowLabel(data.window) })}</span>
      </div>
      <Paper elevation={0} className="stats-card stats-card--flush">
        <Table size="small" className="stats-table">
          <TableHead>
            <TableRow>
              <TableCell>{tr("Status")}</TableCell>
              <TableCell>{tr("Screen")}</TableCell>
              {data.internal && <TableCell className="stats-hide-mobile">{tr("Customer")}</TableCell>}
              <TableCell>{tr("Uptime 7 days")}</TableCell>
              <TableCell className="stats-hide-mobile">{tr("Uptime 30 days")}</TableCell>
              <TableCell align="right" className="stats-hide-mobile">{tr("Outages 30 days")}</TableCell>
              <TableCell className="stats-hide-mobile">{tr("Last seen")}</TableCell>
              {data.internal && <TableCell className="stats-hide-mobile">{tr("Software")}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id} hover onClick={() => onOpen(s.id)} sx={{ cursor: "pointer" }}>
                <TableCell><StatusBadge kind={s.online ? "online" : s.lastActive ? "offline" : "never"} /></TableCell>
                <TableCell>
                  <div className="stats-name">{s.name || tr("Screen")}</div>
                  {s.location && <div className="stats-muted">{s.location}</div>}
                </TableCell>
                {data.internal && <TableCell className="stats-hide-mobile">{s.customer || "–"}</TableCell>}
                <TableCell><UptimeMeter pct={s.uptime7} /></TableCell>
                <TableCell className="stats-hide-mobile"><UptimeMeter pct={s.uptime30} /></TableCell>
                <TableCell align="right" className="stats-hide-mobile">{formatNumber(s.incidents30)}</TableCell>
                <TableCell className="stats-hide-mobile">{s.online ? tr("Now") : formatAgo(s.lastActive, data.generatedAt)}</TableCell>
                {data.internal && <TableCell className="stats-hide-mobile">{s.software || "–"}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && <p className="stats-muted" style={{ padding: 16 }}>{tr("No screens found.")}</p>}
      </Paper>
    </div>
  );
}

function Statistics({ user }) {
  const contextUserId = useContextUserId();
  const [config, setConfig] = useState(null);
  const [view, setView] = useState("internal");
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openScreen, setOpenScreen] = useState(null);

  useEffect(() => {
    if (!contextUserId) return;
    let active = true;
    api
      .get(`/kpi/config/${contextUserId}`)
      .then((r) => { if (active) setConfig(r.data); })
      .catch(() => { if (active) setConfig({ visible: false }); });
    return () => { active = false; };
  }, [contextUserId]);

  const apiView = config?.staff && view === "customer" ? "customer" : undefined;

  useEffect(() => {
    if (!config?.visible) return;
    let active = true;
    setData(null);
    setError("");
    api
      .get(`/kpi/overview/${contextUserId}`, { params: { view: apiView } })
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => { if (active) setError(e?.response?.data?.message || tr("Could not load statistics.")); });
    return () => { active = false; };
  }, [config, contextUserId, apiView]);

  const internal = Boolean(data?.internal);
  const activeTab = !internal && tab === "alerts" ? "overview" : tab;

  return (
    <Layout title={tr("Statistics")} user={user}>
      <Box className="stats-page">
        <header className="stats-hero">
          <div>
            <h1>{tr("Statistics")}</h1>
            <p>{tr("Uptime and problems for your screens.")}</p>
          </div>
          {config?.staff && (
            <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => v && setView(v)} aria-label={tr("View")}>
              <ToggleButton value="internal">{tr("Internal view")}</ToggleButton>
              <ToggleButton value="customer">{tr("Customer view")}</ToggleButton>
            </ToggleButtonGroup>
          )}
        </header>

        {config && !config.visible && (
          <Alert severity="info">{tr("Statistics are not switched on for your account yet.")}</Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {config?.visible && !data && !error && <Box sx={{ p: 6, textAlign: "center" }}><CircularProgress /></Box>}

        {data && (
          <>
            {internal && (
              <Alert severity="warning" className="stats-internal-banner">
                {tr("Internal view: shows all customers, MAC addresses and player problems. Customers never see this.")}
              </Alert>
            )}
            <Tabs value={activeTab} onChange={(e, v) => setTab(v)} className="stats-tabs" variant="scrollable" allowScrollButtonsMobile>
              <Tab value="overview" label={tr("Overview")} />
              <Tab value="screens" label={tr("Screens")} />
              {internal && <Tab value="alerts" label={tr("Alert log")} />}
            </Tabs>
            {activeTab === "overview" && <Overview data={data} onOpen={setOpenScreen} />}
            {activeTab === "screens" && <ScreensTab data={data} onOpen={setOpenScreen} />}
            {activeTab === "alerts" && <AlertsTab contextUserId={contextUserId} onOpenScreen={setOpenScreen} />}
          </>
        )}

        <ScreenDetail
          contextUserId={contextUserId}
          screenId={openScreen}
          view={apiView}
          onClose={() => setOpenScreen(null)}
        />
      </Box>
    </Layout>
  );
}

export default Statistics;
